import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import './RiskAnalysis.css'; // Reuse the same styles

// Get the main platform URL based on environment
const getMainPlatformUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  return 'https://ai.app.platform.halisoft.biz';
};

const HaliLeads = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuotaAlert, setShowQuotaAlert] = useState(false);

  // Module-specific config
  const moduleConfig = {
    name: 'HaliLeads',
    title: 'Leads Agent',
    description: 'AI lead qualification and scoring for sales teams',
    icon: '🎯',
    color: '#8b5cf6',
    iframePath: '/hali-leads-agent', // Path on main platform
    warningThreshold: 80,
    criticalThreshold: 95
  };

  const loadUserData = useCallback(async () => {
    try {
      // Check URL parameters first (passed from main platform)
      const urlParams = new URLSearchParams(window.location.search);
      const urlEmail = urlParams.get('email');
      const urlToken = urlParams.get('token');
      
      // If auth comes from URL, store it for future use
      if (urlEmail) {
        localStorage.setItem('halisoft_email', urlEmail);
        console.log('[HaliLeads] Auth received from URL:', urlEmail);
      }
      if (urlToken) {
        localStorage.setItem('halisoft_token', urlToken);
      }
      
      // Check if user is authenticated - check localStorage
      const token = localStorage.getItem('halisoft_token');
      const storedUser = localStorage.getItem('halisoft_user');
      const storedEmail = localStorage.getItem('halisoft_email');
      
      // Use URL email first, then localStorage
      let email = urlEmail || storedEmail;
      if (!email && storedUser) {
        try {
          email = JSON.parse(storedUser)?.email;
        } catch (e) { /* ignore */ }
      }
      
      // If no authentication at all, redirect to login
      if (!token && !storedUser && !storedEmail && !urlEmail) {
        console.log('[HaliLeads] No authentication found, redirecting to login');
        navigate('/login?redirect=/hali-leads');
        return;
      }
      
      // Try to get user from API
      const userRes = await api.get('/api/user/me').catch((err) => {
        // If 401 but we have email from URL, continue with that
        if (err.response?.status === 401 && email) {
          return { data: { email } };
        }
        if (err.response?.status === 401) {
          throw err;
        }
        // Otherwise use stored email
        return { data: { email: email || 'Guest' } };
      });
      
      const userEmail = userRes.data?.email || email;
      if (!userEmail || userEmail === 'Guest') {
        console.log('[HaliLeads] No valid email, redirecting to login');
        navigate('/login?redirect=/hali-leads');
        return;
      }
      
      // Module-specific: HaliLeads has its own plan/quota
      const moduleParam = 'hali-leads';
      const [quotaRes, subRes] = await Promise.all([
        api.get(`/api/user/quota?email=${encodeURIComponent(userEmail)}&module=${encodeURIComponent(moduleParam)}`),
        api.get(`/api/subscriptions/current?email=${encodeURIComponent(userEmail)}&module=${encodeURIComponent(moduleParam)}`).catch(() => ({ data: null }))
      ]);

      setUser(userRes.data);
      setSubscription(subRes.data);
      setQuota(quotaRes.data);

      // Show alert if quota is low
      if (quotaRes.data.percentageUsed >= moduleConfig.warningThreshold) {
        setShowQuotaAlert(true);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.response?.status === 401) {
        // Clear stale data and redirect to login
        localStorage.removeItem('halisoft_token');
        localStorage.removeItem('halisoft_user');
        localStorage.removeItem('halisoft_email');
        navigate('/login?redirect=/hali-leads');
        return;
      }
      // For other errors, show error state
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [navigate, moduleConfig.warningThreshold]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
      localStorage.removeItem('halisoft_user');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  if (loading) {
    return (
      <div className="risk-analysis-loading">
        <div className="spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const quotaPercentage = quota ? (quota.used / quota.limit) * 100 : 0;
  const isQuotaLow = quotaPercentage >= moduleConfig.warningThreshold;
  const isQuotaCritical = quotaPercentage >= moduleConfig.criticalThreshold;
  const mainPlatformUrl = getMainPlatformUrl();

  return (
    <div className="risk-analysis-page">
      {/* Top Navigation Bar */}
      <nav className="risk-analysis-nav" style={{ background: `linear-gradient(135deg, ${moduleConfig.color}, ${moduleConfig.color}dd)` }}>
        <div className="nav-left">
          <div 
            className="logo" 
            onClick={() => window.location.href = `${mainPlatformUrl}/halilab/dashboard`}
            style={{ cursor: 'pointer' }}
          >
            <img src="/logo-halilab.svg" alt="HaliLab" className="logo-image" style={{height: '40px'}} />
          </div>
          <h1>{moduleConfig.icon} {moduleConfig.name}</h1>
        </div>
        <div className="nav-right">
          <button onClick={() => {
            const email = user?.email || localStorage.getItem('halisoft_email') || '';
            navigate(`/subscription${email ? `?email=${encodeURIComponent(email)}` : ''}`);
          }} className="nav-button">
            🏠 Dashboard
          </button>
          <button onClick={() => {
            const email = user?.email || localStorage.getItem('halisoft_email') || '';
            navigate(`/subscription?module=hali-leads${email ? `&email=${encodeURIComponent(email)}` : ''}`);
          }} className="nav-button">
            💳 My Plan
          </button>
          <button onClick={handleLogout} className="nav-button-logout">
            Logout
          </button>
        </div>
      </nav>

      {/* Quota Alert Banner */}
      {showQuotaAlert && quota.remaining > 0 && (
        <div className={`quota-alert-banner ${isQuotaCritical ? 'critical' : 'warning'}`}>
          <span className="alert-icon">
            {isQuotaCritical ? '🚨' : '⚠️'}
          </span>
          <span className="alert-text">
            {isQuotaCritical
              ? `Warning! You only have ${quota.remaining} leads left this month.`
              : `Low quota: ${quota.remaining} leads remaining this month.`}
          </span>
          <button
            onClick={() => navigate('/subscription?module=hali-leads')}
            className="alert-upgrade-btn"
          >
            ⚡ Upgrade
          </button>
          <button
            onClick={() => setShowQuotaAlert(false)}
            className="alert-close-btn"
          >
            ✕
          </button>
        </div>
      )}

      {/* Quota Exceeded Banner */}
      {quota && quota.remaining === 0 && (
        <div className="quota-exceeded-banner">
          <div className="exceeded-content">
            <span className="exceeded-icon">🚫</span>
            <div className="exceeded-text">
              <h3>Quota Exhausted</h3>
              <p>
                You have used all your {quota.limit} leads this month.
                Upgrade to a higher plan to continue.
              </p>
            </div>
            <button
              onClick={() => navigate('/subscription?module=hali-leads')}
              className="exceeded-upgrade-btn"
            >
              🚀 View Plans
            </button>
          </div>
        </div>
      )}

      <div className="risk-analysis-container">
        {/* Sidebar with User Info */}
        <aside className="sidebar">
          <div className="user-info-card">
            <h3>👤 Your Account</h3>
            <div className="user-details">
              <p className="user-company">{user?.companyName}</p>
              <p className="user-email">{user?.email}</p>
            </div>
          </div>

          <div className="quota-card">
            <h3>{moduleConfig.icon} Monthly Quota</h3>
            <div className="quota-display">
              <div className="quota-number">
                <span className="quota-remaining" style={{ color: moduleConfig.color }}>{quota?.remaining || 0}</span>
                <span className="quota-total">/ {quota?.limit || 0}</span>
              </div>
              <p className="quota-label">leads remaining</p>
            </div>

            <div className="quota-progress-bar">
              <div
                className={`quota-progress-fill ${
                  isQuotaCritical ? 'critical' : isQuotaLow ? 'warning' : 'normal'
                }`}
                style={{ 
                  width: `${Math.min(quotaPercentage, 100)}%`,
                  background: isQuotaCritical ? '#ef4444' : isQuotaLow ? '#f59e0b' : moduleConfig.color
                }}
              />
            </div>

            <div className="quota-stats">
              <div className="stat">
                <span className="stat-label">Used</span>
                <span className="stat-value">{quota?.used || 0}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Percentage</span>
                <span className="stat-value">{quotaPercentage.toFixed(0)}%</span>
              </div>
            </div>
          </div>

          <div className="plan-card">
            <h3>💎 Current Plan</h3>
            <div className="plan-info">
              <p className="plan-name">{subscription?.name || quota?.plan || 'FREE'}</p>
              <p className="plan-price">
                {subscription?.slug === 'enterprise' ? 'Customised pricing' :
                 parseFloat(subscription?.price) > 0 
                  ? `$${parseFloat(subscription.price).toFixed(2)}/month`
                  : 'Free'}
              </p>
            </div>
            <button
              onClick={() => navigate('/subscription?module=hali-leads')}
              className="btn-manage-plan"
              style={{ background: moduleConfig.color }}
            >
              Manage Subscription
            </button>
          </div>

          <div className="help-card">
            <h4>💡 Need Help?</h4>
            <p>Questions about lead qualification? Contact our support team.</p>
            <a href="mailto:support@halisoft.biz">support@halisoft.biz</a>
          </div>
        </aside>

        {/* Main Content - iFrame to HaliLeads Service */}
        <main className="main-content">
          {quota && quota.remaining > 0 ? (
            <div className="iframe-container">
              <iframe
                src={`${mainPlatformUrl}${moduleConfig.iframePath}?email=${encodeURIComponent(user?.email || localStorage.getItem('halisoft_email') || '')}`}
                title={moduleConfig.name}
                className="risk-analysis-iframe"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                allow="clipboard-write"
              />
            </div>
          ) : (
            <div className="service-blocked">
              <div className="blocked-icon">🚫</div>
              <h2>Service Blocked</h2>
              <p>You have exhausted your monthly quota. Upgrade your plan to continue using {moduleConfig.name}.</p>
              <p className="blocked-hint">Your quota will reset on the 1st of next month.</p>
              <button
                onClick={() => navigate('/subscription?module=hali-leads')}
                className="btn-upgrade-now"
                style={{ background: moduleConfig.color }}
              >
                🚀 Upgrade Now
              </button>
            </div>
          )}
        </main>
      </div>

      {/* Floating Chat Badge - Opens HaliGuide on main platform */}
      <button
        onClick={() => {
          const width = 420;
          const height = 700;
          const left = window.screenX + 50;
          const top = window.screenY + 100;
          window.open(
            `${mainPlatformUrl}/landing`,
            'HaliGuide',
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
          );
        }}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 1500,
          background: `linear-gradient(135deg, ${moduleConfig.color}90, ${moduleConfig.color})`,
          border: 'none',
          padding: '14px 20px',
          borderRadius: '50px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: `0 8px 32px ${moduleConfig.color}50`,
          transition: 'all 0.3s ease',
          color: 'white',
          fontWeight: '600',
          fontSize: '0.95rem'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = `0 12px 40px ${moduleConfig.color}60`;
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = `0 8px 32px ${moduleConfig.color}50`;
        }}
        title="Chat with HaliGuide"
      >
        <span style={{ fontSize: '1.2rem' }}>💬</span>
        <span>Need Help?</span>
      </button>
    </div>
  );
};

export default HaliLeads;
